import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, Star, Zap, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";

const ComparisonSection = () => {
  const [selectedPlan, setSelectedPlan] = useState("quick");
  const navigate = useNavigate();
  const plans = {
    quick: {
      name: "Quick SMS",
      description: "Perfect for individual messages and urgent notifications",
      icon: Zap,
      color: "from-red-500 to-red-600",
      bgColor: "from-red-50 to-red-100",
      features: [
        { name: "Instant Delivery", included: true, highlight: true },
        { name: "Single Recipient", included: true },
        { name: "Bulk Sending", included: false },
        { name: "Priority Support", included: false },
      ],
      price: "GH₵5",
      unit: "per message",
      cta: "Start Quick SMS",
      ctaLink: "/login",
    },
    bulk: {
      name: "Bulk SMS",
      description: "Ideal for marketing campaigns and mass communications",
      icon: Users,
      color: "from-blue-500 to-blue-600",
      bgColor: "from-blue-50 to-blue-100",
      features: [
        { name: "Instant Delivery", included: true, highlight: true },
        { name: "Single Recipient", included: true },
        { name: "Bulk Sending", included: true, highlight: true },
        { name: "Priority Support", included: true },
      ],
      price: "GH₵3",
      unit: "per message",
      cta: "Start Bulk SMS",
      ctaLink: "/login",
    },
  };

  const comparisonFeatures = [
    "Message Delivery Speed",
    "Recipient Limit",
    "Delivery Confirmation",
    "24/7 Support",
  ];

  const comparisonData = {
    quick: [true, "Single", true, true],
    bulk: [true, "Unlimited", true, true],
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="container px-4 mx-auto">
        {/* Header */}
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="mb-16 text-center"
        >
          <h2 className="mb-6 text-4xl font-bold text-gray-900 md:text-5xl">
            Choose Your
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-blue-600">
              Perfect Solution
            </span>
          </h2>
          <p className="mx-auto max-w-3xl text-xl text-gray-600">
            Compare our SMS solutions and find the perfect fit for your
            messaging needs
          </p>
        </motion.div>

        {/* Plan Selector */}
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="flex justify-center mb-12"
        >
          <div className="p-2 bg-white rounded-2xl border border-gray-200 shadow-lg">
            {Object.entries(plans).map(([key, plan]) => (
              <motion.button
                key={key}
                onClick={() => setSelectedPlan(key)}
                className={`px-8 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  selectedPlan === key
                    ? `bg-gradient-to-r ${plan.color} text-white shadow-lg`
                    : "text-gray-600 hover:text-gray-900"
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {plan.name}
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Selected Plan Details */}
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedPlan}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            transition={{ duration: 0.5 }}
            className="mb-16"
          >
            <div className="mx-auto max-w-4xl">
              <div
                className={`rounded-3xl p-8 md:p-12 bg-gradient-to-br ${plans[selectedPlan].bgColor} border border-gray-200 shadow-xl`}
              >
                <div className="mb-8 text-center">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className={`w-20 h-20 rounded-2xl bg-gradient-to-r ${plans[selectedPlan].color} flex items-center justify-center mx-auto mb-6`}
                  >
                    {React.createElement(plans[selectedPlan].icon, {
                      className: "w-10 h-10 text-white",
                    })}
                  </motion.div>

                  <h3 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">
                    {plans[selectedPlan].name}
                  </h3>

                  <p className="mb-8 text-xl text-gray-600">
                    {plans[selectedPlan].description}
                  </p>

                  <motion.button
                    onClick={() => navigate(plans[selectedPlan].ctaLink)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`px-8 py-4 bg-gradient-to-r ${plans[selectedPlan].color} text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300`}
                  >
                    {plans[selectedPlan].cta}
                  </motion.button>
                </div>

                <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                  className="grid grid-cols-1 gap-6 md:grid-cols-2"
                >
                  {plans[selectedPlan].features.map((feature, index) => (
                    <motion.div
                      key={index}
                      variants={itemVariants}
                      className={`flex items-center space-x-3 p-4 rounded-xl ${
                        feature.highlight
                          ? "bg-white shadow-md border-2 border-red-200"
                          : "bg-white/50"
                      }`}
                    >
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center ${
                          feature.included
                            ? "bg-red-500 text-white"
                            : "bg-gray-300 text-gray-600"
                        }`}
                      >
                        {feature.included ? (
                          <Check className="w-4 h-4" />
                        ) : (
                          <X className="w-4 h-4" />
                        )}
                      </div>
                      <span
                        className={`font-medium ${
                          feature.highlight ? "text-red-700" : "text-gray-700"
                        }`}
                      >
                        {feature.name}
                      </span>
                      {feature.highlight && (
                        <Star className="ml-auto w-4 h-4 text-yellow-500" />
                      )}
                    </motion.div>
                  ))}
                </motion.div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Detailed Comparison Table */}
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="overflow-hidden mx-auto max-w-6xl"
        >
          <div className="overflow-hidden bg-white rounded-3xl border border-gray-200 shadow-xl">
            <div className="p-6 bg-gradient-to-r from-gray-900 to-gray-800">
              <h3 className="text-2xl font-bold text-center text-white">
                Detailed Feature Comparison
              </h3>
            </div>

            <div className="overflow-x-auto max-w-full">
              <table className="w-full min-w-[600px]">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-sm font-semibold text-left text-gray-900">
                      Feature
                    </th>
                    <th className="px-6 py-4 text-sm font-semibold text-center text-red-600">
                      Quick SMS
                    </th>
                    <th className="px-6 py-4 text-sm font-semibold text-center text-blue-600">
                      Bulk SMS
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {comparisonFeatures.map((feature, index) => (
                    <motion.tr
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.05 }}
                      className="transition-colors duration-200 hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {feature}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {comparisonData.quick[index] === true ? (
                          <Check className="mx-auto w-5 h-5 text-red-500" />
                        ) : comparisonData.quick[index] === false ? (
                          <X className="mx-auto w-5 h-5 text-red-500" />
                        ) : (
                          <span className="text-sm text-gray-600">
                            {comparisonData.quick[index]}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {comparisonData.bulk[index] === true ? (
                          <Check className="mx-auto w-5 h-5 text-red-500" />
                        ) : comparisonData.bulk[index] === false ? (
                          <X className="mx-auto w-5 h-5 text-red-500" />
                        ) : (
                          <span className="text-sm text-gray-600">
                            {comparisonData.bulk[index]}
                          </span>
                        )}
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-16 text-center"
        >
          <h3 className="mb-4 text-2xl font-bold text-gray-900">
            Ready to Get Started?
          </h3>
          <p className="mb-8 text-lg text-gray-600">
            Join thousands of businesses already using our SMS solutions
          </p>
          <div className="flex flex-col gap-4 justify-center sm:flex-row">
            <motion.button
              onClick={() => navigate(plans[selectedPlan].ctaLink)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 font-semibold text-white bg-gradient-to-r from-red-500 to-red-600 rounded-xl shadow-lg transition-all duration-300 hover:shadow-xl"
            >
              Start Your Journey
            </motion.button>
            <motion.button
              onClick={() => navigate("/contact-us")}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 font-semibold text-gray-700 rounded-xl border-2 border-gray-300 transition-all duration-300 hover:bg-gray-50"
            >
              Contact Sales
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ComparisonSection;
